import React, { useEffect, useState } from "react";
import ReactJson from "react-json-view";
import { useParams } from "react-router";
import { useNavigate } from "react-router-dom";
import { useSelector, shallowEqual, useDispatch } from "react-redux";
import {
  SG_FETCH_SURVEY_STANDARD,
  SG_ADD_SURVEY_STANDARD,
  SG_EDIT_SURVEY_STANDARD,
  SG_DELETE_SURVEY_STANDARD,
} from "constants/actions";
import { format } from "date-fns";
import { BasicErrorMessage } from "utilities/ErrorMessages";

// shadcn/ui components
import { Button } from "components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import {
  Select as UISelect,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "components/ui/alert-dialog";
import { Separator } from "components/ui/separator";
import { Alert, AlertDescription } from "components/ui/alert";

//
const Details = ({ employee_record_value, setReload }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const [templateId, setTemplateId] = useState(false);
  const [templateDetails, setTemplateDetails] = useState({
    name: null,
  });
  const [orgSelected, setOrgSelected] = useState([]);
  const [valueSelected, setValueSelected] = useState([]);

  const [formErrors, setFormErrors] = useState([]);
  const [pristine, setPristine] = useState(true);
  const [valueChanged, setValueChanged] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const [responseEditor, setResponseEditor] = useState({});
  const [repeatEvery, setRepeatEvery] = useState("Never");
  const [repeatHrs, setRepeatHrs] = useState(0);
  const [responseText, setResponseText] = useState("");

  function BackArrow() {
    navigate(`/app/survey-standard`);
  }

  useEffect(() => {
    setTemplateId(id);
  }, [id]);

  useEffect(() => {
    if (templateId) {
      dispatch({
        type: SG_FETCH_SURVEY_STANDARD,
        payload: { id: templateId },
      });
    }
  }, [dispatch, templateId]);

  const { get_survey_standard } = useSelector(
    (state) => ({
      get_survey_standard: state.survey_standard,
    }),
    shallowEqual
  );

  useEffect(() => {
    if (get_survey_standard?.[templateId]) {
      setTemplateDetails(get_survey_standard?.[templateId]);
      const _details = get_survey_standard?.[templateId] || {};
      setResponseEditor(_details?.response || {});
      try {
        setResponseText(JSON.stringify(_details?.response || {}, null, 2));
      } catch (e) {
        setResponseText("");
      }
      const _repeatEvery = _details?.repeat_every || "Never";
      setRepeatEvery(_repeatEvery);
      setRepeatHrs(_repeatEvery === "Never" ? 0 : (_details?.repeat_hrs ?? 168));
    }
  }, [get_survey_standard, templateId]);

  useEffect(() => {
    if (get_survey_standard?.created?.id) {
      navigate(
        `/app/survey-standard/details/${get_survey_standard?.created?.id}`
      );
    }
  }, [get_survey_standard, navigate]);

  useEffect(() => {
    if (get_survey_standard?.deleted) {
      setReload(Math.random());
      const timer1 = setTimeout(() => {
        navigate(`/app/survey-standard`);
      }, 500);
      return () => clearTimeout(timer1);
    }
  }, [get_survey_standard, navigate, setReload]);

  function DeleteQuestion() {
    if (templateId) {
      dispatch({
        type: SG_DELETE_SURVEY_STANDARD,
        payload: {
          id: templateId,
        },
      });

      // navigate(`/app/survey-standard?reload=true`)
    }
  }
  // function UpdateQuestions() {
  //   setPristine(false)
  //   if (formErrors.length === 0) {
  //     if (templateId) {
  //       dispatch({
  //         type: SG_EDIT_SURVEY_STANDARD,
  //         payload: {
  //           id: templateId,
  //           organization: orgSelected?.id,
  //           name: templateDetails?.name,
  //           replace_by: templateDetails?.replace_by,
  //           employee_record_value: valueSelected?.id,
  //         }
  //       });
  //       navigate(`/app/survey-standard/detail/${templateId}`)
  //     }
  //     setPristine(true)
  //   }
  // }

  function onJsonChange(edit) {
    if (edit && edit.updated_src !== undefined) {
      setResponseEditor(edit.updated_src);
      try {
        setResponseText(JSON.stringify(edit.updated_src, null, 2));
      } catch (e) {}
      setValueChanged(Math.random());
    }
  }

  function onRepeatEveryChange(option) {
    const value = option?.value;
    setRepeatEvery(value);
    if (value === "Never") {
      setRepeatHrs(0);
    } else if (value === "Week" && (repeatHrs === 0 || repeatHrs == null)) {
      setRepeatHrs(168);
    }
  }

  function loadJsonFromText() {
    try {
      const parsed = JSON.parse(responseText || "{}");
      setResponseEditor(parsed);
      setValueChanged(Math.random());
    } catch (e) {
      alert("Invalid JSON. Please fix and try again.");
    }
  }

  function SaveStandard() {
    if (!templateId) return;
    dispatch({
      type: SG_EDIT_SURVEY_STANDARD,
      payload: {
        id: templateId,
        response: responseEditor,
        repeat_every: repeatEvery,
        repeat_hrs: repeatEvery === "Never" ? 0 : Number(repeatHrs || 0),
      },
    });
  }

  useEffect(() => {
    const ErrorList = [];
    if (!templateDetails?.name) {
      ErrorList.push("Enter a Name");
    }
    if (!valueSelected?.id) {
      ErrorList.push("Select a Value Name");
    }
    if (!orgSelected?.id) {
      ErrorList.push("Select an Organization");
    }

    setFormErrors(ErrorList);
  }, [templateDetails, valueSelected, orgSelected, valueChanged]);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" className="px-2" onClick={BackArrow}>
          ← Back
        </Button>
        <h2 className="text-xl font-semibold">
          {templateId ? `Edit Details of ${templateDetails?.name}` : "Add New List Value"}
        </h2>
      </div>

      {formErrors.length > 0 && !pristine && (
        <Alert variant="destructive">
          <AlertDescription>
            <div className="font-medium mb-2">We need these details:</div>
            <ul className="list-disc pl-5 space-y-1">
              {formErrors.map((err, idx) => (
                <li key={idx}>{err}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      <div>
        <BasicErrorMessage />
      </div>

      {templateId && (
        <Card>
          <CardHeader>
            <CardTitle>Standard Info</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-6 md:grid-cols-2">
            <div>
              <Label className="mb-1 block">Name</Label>
              <div className="text-sm text-muted-foreground">{templateDetails?.name || "—"}</div>
            </div>
            <div className="md:col-span-2">
              <Label className="mb-2 block">Questions (sort order)</Label>
              <div className="rounded-md border p-3">
                <ReactJson src={templateDetails?.question_sort_order} collapsed={1} displayDataTypes={false} enableClipboard={false} name={false} />
              </div>
            </div>
            <div>
              <Label className="mb-1 block">Next Run At</Label>
              <div className="text-sm text-muted-foreground">
                {templateDetails?.created_at ?
                  format(new Date(templateDetails?.run_next_on), "dd-MMM-yyyy hh:mm a") : "—"}
              </div>
            </div>
            <div>
              <Label className="mb-1 block">Updated At</Label>
              <div className="text-sm text-muted-foreground">
                {templateDetails?.created_at ?
                  format(new Date(templateDetails?.updated_at), "dd-MMM-yyyy hh:mm a") : "—"}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">Manage schedule and payload.</div>
        <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <Button variant="destructive" onClick={() => setConfirmOpen(true)}>Delete</Button>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete this standard?</AlertDialogTitle>
              <AlertDialogDescription>
                This action cannot be undone. This will permanently delete the survey standard.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={DeleteQuestion}>Delete</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Schedule</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-6 md:grid-cols-2">
          <div className="space-y-2 max-w-xs">
            <Label>Repeat</Label>
            <UISelect value={repeatEvery} onValueChange={(v) => onRepeatEveryChange({ value: v })}>
              <SelectTrigger>
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Never">Never</SelectItem>
                <SelectItem value="Week">Week</SelectItem>
              </SelectContent>
            </UISelect>
          </div>

          <div className="space-y-2 max-w-xs">
            <Label>Repeat Hours</Label>
            <Input
              type="number"
              min={0}
              disabled={repeatEvery === "Never"}
              value={repeatEvery === "Never" ? 0 : repeatHrs}
              onChange={(e) => setRepeatHrs(Number(e.target.value))}
            />
            <div className="text-xs text-muted-foreground">Default week = 168 hours.</div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Response JSON</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-6 md:grid-cols-2">
            <div className="space-y-2">
              <Label>Text (paste JSON)</Label>
              <textarea
                className="w-full min-h-[260px] font-mono text-sm rounded-md border p-3 focus:outline-none focus:ring-2 focus:ring-ring"
                value={responseText}
                onChange={(e) => setResponseText(e.target.value)}
              />
              <div className="flex gap-2">
                <Button variant="secondary" onClick={() => setResponseText(JSON.stringify(responseEditor, null, 2))}>Copy from Editor</Button>
                <Button onClick={loadJsonFromText}>Load into Editor</Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Visual</Label>
              <div className="rounded-md border p-3">
                <ReactJson
                  src={responseEditor}
                  onEdit={onJsonChange}
                  onAdd={onJsonChange}
                  onDelete={onJsonChange}
                  displayDataTypes={false}
                  enableClipboard={false}
                  name={false}
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="sticky bottom-0 z-10 bg-background/60 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-t py-3">
        <div className="container mx-auto flex justify-end">
          <Button onClick={SaveStandard}>Save</Button>
        </div>
      </div>
    </div>
  );
};

export default Details;
